import Chip from "@hyro-components/Chip";
import AddIcon from "@mui/icons-material/Add";
import { CircularProgress, Link, Button, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import CodeIcon from "src/icons/CodeIcon";
import ExternalIcon from "src/icons/ExternalIcon";
import PhoneRingingIcon from "src/icons/PhoneRingingIcon";
import PreviewIcon from "src/icons/PreviewIcon";
import {
  branchDeployerErrorSelector, branchDeployerRowsSelector, createBranchDeployerSelector,
  deleteBranchDeployerSelector,
  editBranchDeployerSelector, getBranchDeployerRowsSelector,
  getBranchDeployerStatusSelector,
  setBranchDeployerErrorSelector
} from "src/stores/selectors/branchDeployerSelector";
import {
  BRANCH_DEPLOYER_INITIAL_DATA,
  BRANCH_DEPLOYER_STORAGE_KEY,
  COLUMNS, COPY_SNIPPET_SUCCESS_MESSAGE,
  DIALOG_TYPES, KEYS_TO_FILTER, KEYS_TO_FILTER_WITH_CONFIG_ID, BD_STATUS_TO_CHIP, STATUS_TYPES, FILTERS
} from "../../components/branch-deployer/BranchDeployer.consts";
import BranchDeployerDialog from "../../components/branch-deployer/BranchDeployerDialog";
import HyroDialog from "@hyro-components/Dialogs/HyroUiDialog";
import { HyroLoader, HyroIconButton } from "@hyro-components";
import HyroFeedbackMessage from "@hyro-components/HyroFeedbackMessage";
import { makeBranchDeployerSnippet } from "../../utils/helpers/snippetGenerator";
import useAppStore from "../../stores/appStore";
import { filterObjectByKeys } from "@utils";
import { getFlag } from "src/utils/helpers/ldClient";
import { useFetchDashboardSettings } from "src/queries/hooks/settings";
import { ICON_BUTTON_VARIANTS, ICON_BUTTON_SIZES } from "@hyro-components/hyro-icon-button/HyroIconButton.consts";
import HyroTableContainer from "@hyro-components/hyro-table-container/HyroTableContainer";

const initialDialogState = {
  open: false,
  type: ""
};

const BranchDeployer = () => {
  const branchDeployerRows = useAppStore(branchDeployerRowsSelector);
  const getBranchDeployerRows = useAppStore(getBranchDeployerRowsSelector);
  const createBranchDeployer = useAppStore(createBranchDeployerSelector);
  const editBranchDeployer = useAppStore(editBranchDeployerSelector);
  const deleteBranchDeployer = useAppStore(deleteBranchDeployerSelector);
  const getBranchDeployerStatus = useAppStore(getBranchDeployerStatusSelector);
  const error = useAppStore(branchDeployerErrorSelector);
  const setError = useAppStore(setBranchDeployerErrorSelector);
  const [branchDeployerDialog, setBranchDeployerDialog] = useState({});
  const dashboardSettings = useFetchDashboardSettings()?.data;

  const handleBranchDeployerDialogClose = () => {
    setBranchDeployerDialog(initialDialogState);
  };

  const [feedbackConfig, setFeedbackConfig] = useState({
    show: false,
    message: "",
    severity: "success"
  });

  useEffect(() => {
    getBranchDeployerRows();
  }, [getBranchDeployerRows]);

  const getInitialDataFromLocalStorage = () => {
    const savedData = localStorage.getItem(BRANCH_DEPLOYER_STORAGE_KEY);
    if (savedData) {
      const parsedData = JSON.parse(savedData);
      const isConfigIdFeatureFlagOn = getFlag("deploy-branch-deployer-with-config-id");
      const keysToFilter = isConfigIdFeatureFlagOn ? KEYS_TO_FILTER_WITH_CONFIG_ID : KEYS_TO_FILTER;
      const migratedData = filterObjectByKeys(parsedData, keysToFilter);
      localStorage.setItem(BRANCH_DEPLOYER_STORAGE_KEY, JSON.stringify(migratedData));
      return migratedData;
    }
    return BRANCH_DEPLOYER_INITIAL_DATA;
  };

  const onSubmit = async (data, type) => {
    const isConfigIdFeatureFlagOn = getFlag("deploy-branch-deployer-with-config-id");
    const keysToFilter = isConfigIdFeatureFlagOn ? KEYS_TO_FILTER_WITH_CONFIG_ID : KEYS_TO_FILTER;
    if (type === DIALOG_TYPES.create.id) {
      const error = await createBranchDeployer(data);
      if (!error) {
        localStorage.removeItem(BRANCH_DEPLOYER_STORAGE_KEY);
      }
    } else if (type === DIALOG_TYPES.edit.id) {
      const filteredData = filterObjectByKeys(data, keysToFilter);
      await editBranchDeployer(filteredData);
    } else if (type === DIALOG_TYPES.reactivate.id) {
      const filteredData = filterObjectByKeys(data, keysToFilter);
      await editBranchDeployer(filteredData);
    } else if (type === DIALOG_TYPES.deactivate.id) {
      await deleteBranchDeployer(data.releaseName);
    }
    setBranchDeployerDialog({ ...branchDeployerDialog, open: false });
  };
  if (!branchDeployerRows) return <HyroLoader />;

  const openSnackbar = () => {
    setFeedbackConfig({
      show: true,
      message: COPY_SNIPPET_SUCCESS_MESSAGE,
      severity: "success"
    });
  };

  const getRowActions = (row) => {
    const actions = [
      {
        id: DIALOG_TYPES.edit.id,
        display: DIALOG_TYPES.edit.display,
        run: (row) => {
          setBranchDeployerDialog({
            ...branchDeployerDialog,
            open: true,
            type: DIALOG_TYPES.edit.id,
            data: row
          });
        }
      },
      {
        id: DIALOG_TYPES.refresh.id,
        display: DIALOG_TYPES.refresh.display,
        run: (row) => {
          getBranchDeployerStatus(row.releaseName);
        }
      },
      {
        id: DIALOG_TYPES.viewDetails.id,
        display: DIALOG_TYPES.viewDetails.display,
        run: (row) => {
          setBranchDeployerDialog({
            ...branchDeployerDialog,
            open: true,
            type: DIALOG_TYPES.viewDetails.id,
            data: row
          });
        }
      },
      {
        id: DIALOG_TYPES.reactivate.id,
        display: DIALOG_TYPES.reactivate.display,
        run: (row) => {
          setBranchDeployerDialog({
            ...branchDeployerDialog,
            open: true,
            type: DIALOG_TYPES.reactivate.id,
            data: row
          });
        }
      },
      {
        id: DIALOG_TYPES.deactivate.id,
        display: DIALOG_TYPES.deactivate.display,
        run: (row) => {
          setBranchDeployerDialog({
            ...branchDeployerDialog,
            open: true,
            type: DIALOG_TYPES.deactivate.id,
            data: row
          });
        }
      }
    ];

    if (row.releaseName?.startsWith("bd-") && row.status === STATUS_TYPES.TERMINATED) {
      return actions.filter(({ id }) => id === DIALOG_TYPES.viewDetails.id);
    }

    if (dashboardSettings?.disableBd) {
      return actions.filter(({ id }) => id !== DIALOG_TYPES.reactivate.id && id !== DIALOG_TYPES.edit.id);
    }

    return actions;
  };

  const createNewBranchDeployerButton = (
    <Button
      data-testid="branch-deployer-create-new-button"
      startIcon={<AddIcon />}
      color="primary"
      size="medium"
      variant="contained"
      onClick={() => {
        setBranchDeployerDialog({
          open: true,
          type: DIALOG_TYPES.create.id,
          data: getInitialDataFromLocalStorage()
        });
      }}
    >
      Add New
    </Button>
  );

  return (
    <>
      <HyroTableContainer
        data={branchDeployerRows?.map(row => ({ ...row, id: row._id })) || []}
        header={{
          showDownloadButton: false,
          children: createNewBranchDeployerButton
        }}
        filters={{
          initialFilters: FILTERS
        }}
        pagination={{
          showPagination: true
        }}
        columns={[
          { field: COLUMNS.releaseName.id, headerName: COLUMNS.releaseName.label },
          {
            field: COLUMNS.status.id, headerName: COLUMNS.status.label, renderCell: ({ row }) => {
              const { status } = row;
              return status === DIALOG_TYPES.refresh.id ? <CircularProgress size={30} /> :
                <Chip
                  sx={{ fontSize: "12px" }}
                  size="small"
                  backgroundColorPath={BD_STATUS_TO_CHIP[status].backgroundColorPath}
                  textColorPath={BD_STATUS_TO_CHIP[status].textColorPath}
                  label={status} />;
            }
          },
          { field: COLUMNS.services.id, headerName: COLUMNS.services.label, valueGetter: (services) => services?.join(", ") },
          {
            field: COLUMNS.link.id, headerName: COLUMNS.link.label, sortable: false, width: 125, renderCell: ({ value: link }) => <Link href={link} target="_blank">
              <HyroIconButton variant={ICON_BUTTON_VARIANTS.CLEAR} size={ICON_BUTTON_SIZES.XLARGE}>
                <ExternalIcon strokeOpacity="1" />
              </HyroIconButton>
            </Link>
          },
          {
            field: COLUMNS.previewLink.id, headerName: COLUMNS.previewLink.label, sortable: false, width: 125, renderCell: (params) => {
              const { value: link, row: { channel } } = params;
              return link ? <Link href={link} target="_blank">
                <HyroIconButton variant={ICON_BUTTON_VARIANTS.CLEAR} size={ICON_BUTTON_SIZES.XLARGE}>
                  {channel === "Phone" ? <PhoneRingingIcon /> : <PreviewIcon />}
                </HyroIconButton>
              </Link> : null;
            }
          },
          {
            field: COLUMNS.chat.id, headerName: COLUMNS.chat.label, sortable: false, width: 125, renderCell: (row) => {
              const { url, assistantId } = row.value;
              return url ? <CopyToClipboard text={makeBranchDeployerSnippet(assistantId, url)}>
                <HyroIconButton variant={ICON_BUTTON_VARIANTS.CLEAR} size={ICON_BUTTON_SIZES.XLARGE} onClick={openSnackbar}>
                  <CodeIcon strokeOpacity="1" />
                </HyroIconButton>
              </CopyToClipboard> : null;
            }
          },
          { field: COLUMNS.userEmail.id, headerName: COLUMNS.userEmail.label },
          { field: COLUMNS.editorEmail.id, headerName: COLUMNS.editorEmail.label },
          {
            field: COLUMNS.expired.id,
            headerName: COLUMNS.expired.label,
            renderCell: ({ value: expired }) => expired > 0 ? expired + " hours" : "Expired"
          }
        ]}
        rowActions={(row) => getRowActions(row)}
        testId="branch-deployer"
      />
      {
        branchDeployerDialog.open && (
          <BranchDeployerDialog
            {...branchDeployerDialog}
            onClose={handleBranchDeployerDialogClose}
            onSubmit={onSubmit}
          />
        )
      }
      <HyroDialog
        open={error}
        showCloseButton
        showCancelButton
        variant="transactional"
        title="Something went wrong"
        submitButtonText="Got it"
        handleClose={() => setError(false)}
        handleCancel={() => setError(false)}
        handleSubmit={() => setError(false)}
      >
        <Typography>An unexpected error has occurred. Please try again later. Contact support if the error persists.
        </Typography>
      </HyroDialog >
      <HyroFeedbackMessage
        message={feedbackConfig.message}
        severity={feedbackConfig.severity}
        showFeedback={feedbackConfig.show}
        closeFeedback={() =>
          setFeedbackConfig(state => ({ ...state, show: false }))}
      />
    </>
  )
  ;
}
  ;

export default BranchDeployer;
